import { Button, Text, Flex, Group, Modal, Select, ComboboxItem } from '@mantine/core';
import React, { useState } from 'react';
import useUsers from 'src/admin/users/hooks/useUsers';
import { useUpdateClient } from 'src/clients/hooks/useUpdateClient';
import { Logger } from 'src/utils/Logger';
import { notifications } from '@mantine/notifications';

type Props = {
  clientId: string;
  currentAdvisorId?: string;
  opened: boolean;
  close: () => void;
  onTransferSuccess: () => void;
};

export default function TransferClientModal({
  currentAdvisorId,
  clientId,
  opened,
  close,
  onTransferSuccess,
}: Props) {
  const { users, isLoading: usersLoading, isError: isLoadingUserError } = useUsers();
  const [value, setValue] = useState<ComboboxItem | null>(null);
  const {
    updateClient,
    isLoading: isUpdatingClient,
    error: updateClientError,
    reset,
  } = useUpdateClient();

  const currentAdvisor = users.find(user => user.id === currentAdvisorId);

  const handleSelectAdvisor = (_value: string | null, option: ComboboxItem) => {
    reset();
    setValue(option);
  };

  const updateClientAdvisor = async () => {
    try {
      await updateClient(clientId, { advisorUserId: value?.value });
      close();

      notifications.show({
        title: 'Transfer successful',
        message: `This client has be successfully assigned to ${value?.label}`,
        color: 'green',
      });
      onTransferSuccess();
    } catch (e) {
      Logger.error('Failed to transfer client', e);
    }
  };

  const getErrorMessage = () => {
    if (updateClientError) {
      return 'Failed to transfer client. Please try again.';
    }
    if (isLoadingUserError) {
      return 'Failed to load advisors. Please try again.';
    }
    return null;
  };

  return (
    <Modal opened={opened} onClose={close} title="Transfer client" centered>
      <Flex direction="column" gap="md">
        <Text mb="md">
          Once the transfer is complete, the current advisor may lose access to this client
          depending on their user role.
        </Text>
        {currentAdvisor && (
          <Text mb="md">
            <Text span fw={700}>
              Current advisor:
            </Text>{' '}
            {currentAdvisor.lastName}, {currentAdvisor.firstName}
          </Text>
        )}
        <Select
          label="Select new advisor"
          placeholder={'Select advisor'}
          data={users.map(user => ({
            label: `${user.lastName}, ${user.firstName}`,
            value: user.id,
          }))}
          value={value ? value.value : null}
          onChange={handleSelectAdvisor}
          searchable
          required
          error={getErrorMessage() ? getErrorMessage() : undefined}
        />
        <Group justify="flex-end" mt="md">
          <Button
            type="submit"
            variant="filled-pill-black"
            loading={usersLoading || isUpdatingClient}
            onClick={updateClientAdvisor}
            disabled={!value || isUpdatingClient}
          >
            Transfer
          </Button>
        </Group>
      </Flex>
    </Modal>
  );
}
